import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {QuickSnapMode} from './QuickSnapMode';
import type {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import type {SketchEllipseObject} from '../../mesh/sketch/SketchEllipseObject';
import {isSketchCircleObject} from '../../mesh/guards/isSketchCircleObject';
import {isSketchArcObject} from '../../mesh/guards/isSketchArcObject';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {GeometryInteractionState} from '../interactionState/GeometryInteractionState';

/** 支持中心点显示的形状类型联合。 */
type CenterShape = SketchCircleObject | SketchArcObject | SketchEllipseObject;

/**
 * 中心点捕捉策略。
 * 说明：
 * 1. 普通模式：从活跃草图组的圆心点专属集合中获取候选点，在 15px 阈值内取最近点返回；
 * 2. 快速捕捉模式（QuickSnapMode.Center）：
 *    - 全局搜索最近的圆/圆弧/椭圆（无距离限制），高亮对应边线；
 *    - 返回该形状的中心点，权重 200 确保必定胜出；
 *    - 点击确认后由 SketchDrawingManager 触发 one-shot 清除。
 * 权重：普通 90，快速捕捉 200。
 */
export class CenterPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Center;

    /** 普通模式中心点捕捉权重。端点 100 > 圆心 90 > 中点 80。 */
    private static readonly WEIGHT = 90;
    /** 快速捕捉模式权重，确保必定胜出所有普通策略。 */
    private static readonly QUICK_SNAP_WEIGHT = 200;

    /** 快速捕捉模式下当前高亮的边线，用于 clearPreview() 恢复材质。 */
    private highlightedEdge: AbstractSketchEdgeObject | null = null;

    /** 当前吸附高亮的中心点对象。 */
    private highlightedPoint: SketchPointObject | null = null;

    /** 当前高亮中心点的原始可见性。 */
    private highlightedPointOriginalVisibility: boolean | null = null;

    /**
     * 执行中心点捕捉求解。
     * @param request 点捕捉请求参数。
     * @returns 最近圆心点的捕捉结果；不在阈值内时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        
        if (request.quickSnapMode === QuickSnapMode.Center) {
            return this.resolveQuickSnap(request);
        }

        // 普通模式：清理快速模式残留边线高亮，但不影响点高亮同步。
        this.clearEdgeHighlight();
        const transientModelCenterResult = this.resolveTransientModelCenter(request);
        if (transientModelCenterResult) {
            this.syncSnapHoverState(transientModelCenterResult.targetObject);
            return transientModelCenterResult;
        }

        const collection = this.getActiveCenterPoints();
        if (collection === null) {
            this.syncSnapHoverState(null);
            return null;
        }

        let bestResult: PointSnapResult | null = null;
        let bestPixelDist = this.snapPixelThreshold;

        for (const point of collection) {
            if (this.isSnapTargetExcluded(request, point)) {
                continue;
            }

            const worldPos = point.getPosition();
            const screenPos = this.projectWorldToScreen(worldPos);
            if (screenPos === null) {
                continue;
            }
            const pixelDist = screenPos.distanceTo(request.screenPosition);
            if (pixelDist < bestPixelDist) {
                bestPixelDist = pixelDist;
                bestResult = {
                    strategyType: this.strategyType,
                    position: worldPos.clone(),
                    targetObject: point,
                    weight: CenterPointSnapStrategy.WEIGHT,
                };
            }
        }

        this.syncSnapHoverState(bestResult?.targetObject ?? null);

        return bestResult;
    }

    /**
     * 解析悬停可见模型圆锥边后动态生成的中心点。
     *
     * @param request 当前点捕捉请求。
     * @returns 像素阈值内的模型中心点捕捉结果；未命中时返回 null。
     */
    private resolveTransientModelCenter(request: PointSnapRequest): PointSnapResult | null {
        let bestPoint: SketchPointObject | null = null;
        let bestPixelDistance = this.snapPixelThreshold;

        for (const point of request.transientPointObjects ?? []) {
            if (
                !point.isSketchSessionModelEdgeCenter
                || !point.visible
                || this.isSnapTargetExcluded(request, point)
            ) {
                continue;
            }
            const screenPosition = this.projectWorldToScreen(point.getPosition());
            if (!screenPosition) {
                continue;
            }
            const pixelDistance = screenPosition.distanceTo(request.screenPosition);
            if (pixelDistance < bestPixelDistance) {
                bestPixelDistance = pixelDistance;
                bestPoint = point;
            }
        }

        if (!bestPoint) {
            return null;
        }
        const position = bestPoint.getPosition().clone();
        return {
            strategyType: this.strategyType,
            position,
            indicatorPosition: position.clone(),
            targetObject: bestPoint,
            weight: CenterPointSnapStrategy.WEIGHT,
            showIndicator: true,
        };
    }

    /**
     * 清除快速捕捉高亮预览（恢复边线材质）。
     * 说明：在绘制结束、工具切换或 one-shot 完成后由 PointSnapManager.clearAllPreviews() 调用。
     */
    public override clearPreview(): void {
        this.clearHighlight();
    }

    /**
     * 快速捕捉模式求解：全局找最近圆/圆弧/椭圆，高亮边线，返回中心点。
     * @param request 点捕捉请求参数。
     * @returns 最近圆心点的捕捉结果；无圆类几何时返回 null。
     */
    private resolveQuickSnap(request: PointSnapRequest): PointSnapResult | null {
        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            this.clearHighlight();
            return null;
        }

        // 遍历活跃草图组内所有圆/圆弧/椭圆，找屏幕距离最近的中心点。
        let bestShape: CenterShape | null = null;
        let bestDist = Infinity;

        for (const edge of group.getSketchEdgeObjects()) {
            if (this.isSnapTargetExcluded(request, edge)) {
                continue;
            }
            if (!isSketchCircleObject(edge) && !isSketchArcObject(edge) && !isSketchEllipseObject(edge)) {
                continue;
            }
            const shape = edge as CenterShape;
            // 使用中心点的屏幕坐标作为距离基准。
            const centerPoint = this.findCenterPointForShape(shape, request);
            if (!centerPoint) {
                continue;
            }
            const worldPos = centerPoint.getPosition();
            const screenPos = this.projectWorldToScreen(worldPos);
            if (!screenPos) {
                continue;
            }
            const dist = screenPos.distanceTo(request.screenPosition);
            if (dist < bestDist) {
                bestDist = dist;
                bestShape = shape;
            }
        }

        if (bestShape === null) {
            this.clearHighlight();
            return null;
        }

        // 高亮最近的边线（差量更新，避免频繁切换材质）。
        if (this.highlightedEdge !== bestShape) {
            this.clearHighlight();
            this.viewer?.interactionStateManager?.setSketchObjectState(
                bestShape,
                GeometryInteractionState.Hover,
            );
            this.highlightedEdge = bestShape;
        }

        // 获取中心点对象。
        const centerPoint = this.findCenterPointForShape(bestShape, request);
        if (!centerPoint) {
            this.syncSnapHoverState(null);
            return null;
        }
        this.syncSnapHoverState(centerPoint);
        const worldPos = centerPoint.getPosition();

        return {
            strategyType: this.strategyType,
            position: worldPos.clone(),
            targetObject: centerPoint,
            weight: CenterPointSnapStrategy.QUICK_SNAP_WEIGHT,
        };
    }

    /**
     * 在活跃草图组的圆心点集合中查找与给定形状关联的中心点。
     * @param shape 圆/圆弧/椭圆形状对象。
     * @param request 点捕捉请求，用于排除当前变换域对象。
     * @returns 对应的圆心 SketchPointObject；未找到时返回 null。
     */
    private findCenterPointForShape(shape: CenterShape, request: PointSnapRequest) {
        const collection = this.getActiveCenterPoints();
        if (!collection) return null;
        // 圆心点的 relatedSketchPoints 反向关联圆边，通过 shape.relatedSketchPoints 匹配。
        for (const p of collection) {
            if (this.isSnapTargetExcluded(request, p)) {
                continue;
            }
            if (shape.relatedSketchPoints.has(p)) {
                return p;
            }
        }
        // fallback：直接遍历边的 relatedSketchPoints 中是否包含圆心点。
        for (const p of shape.relatedSketchPoints) {
            if (this.isSnapTargetExcluded(request, p)) {
                continue;
            }
            if (collection.has(p)) {
                return p;
            }
        }
        return null;
    }

    /**
     * 清除当前高亮的边线，恢复默认材质。
     */
    private clearHighlight(): void {
        this.clearEdgeHighlight();
        this.clearPointHighlight();
    }

    /**
     * 清理中心点快速捕捉边线高亮。
     */
    private clearEdgeHighlight(): void {
        if (this.highlightedEdge !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.highlightedEdge);
            this.highlightedEdge = null;
        }
    }

    /**
     * 清理当前中心点高亮，并恢复原始可见性。
     */
    private clearPointHighlight(): void {
        if (this.highlightedPoint !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.highlightedPoint);
            const isTopologyVisibilityManaged = this.highlightedPoint.isEndPoint
                || this.highlightedPoint.isMidPoint
                || this.highlightedPoint.isQuadrantPoint;
            this.highlightedPoint.refreshDefaultVisibilityByTopologyRole();
            if (this.highlightedPointOriginalVisibility !== null && !isTopologyVisibilityManaged) {
                this.highlightedPoint.visible = this.highlightedPointOriginalVisibility;
            }
        }
        this.highlightedPoint = null;
        this.highlightedPointOriginalVisibility = null;
    }

    /**
     * 同步当前命中的中心点移入材质状态。
     * @param targetObject 当前捕捉目标对象。
     */
    private syncSnapHoverState(targetObject: PointSnapResult['targetObject'] | null): void {
        const point = targetObject instanceof SketchPointObject ? targetObject : null;
        if (point === null) {
            this.clearPointHighlight();
            return;
        }

        if (this.highlightedPoint !== point) {
            this.clearPointHighlight();
            this.highlightedPointOriginalVisibility = point.visible;
        }

        point.visible = true;
        this.viewer?.interactionStateManager?.setSketchObjectState(
            point,
            GeometryInteractionState.Hover,
        );
        this.highlightedPoint = point;
    }
}
